// Per-field browser dictation for the SOAP note. core.md §7.5.
//
// The شرح حال screen dictates a WHOLE history and then asks the AI to file it
// into sections (`parseCaseVoice`). This is the other half of the same idea and
// deliberately simpler: the student is already in a field, they know which
// section they are speaking into, and the only useful behaviour is to APPEND the
// words to that field. No model, no cost, no parsing — `SpeechRecognition` and
// a textarea.
//
// It is a hook rather than a panel because the affordance is a small microphone
// on each of four fields, not a sheet: opening a sheet to dictate one sentence
// into «معاینه و یافته‌ها» would close the field the student is looking at.
//
// Firefox has no `SpeechRecognition` at all and no prefix for it, so `supported`
// is false there and the buttons are simply not rendered — never rendered
// disabled, which reads as "broken" rather than "not a thing your browser does".

import React from 'react';

const SPEECH_LANG = 'fa-IR';

export function speechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * @param onCommit (text) => void — called with each FINAL chunk, once. The caller
 *                 appends it to whichever field is being dictated into.
 */
export function useDictation(onCommit) {
  const [field, setField] = React.useState(null); // which field is listening
  const recognizerRef = React.useRef(null);
  const commitRef = React.useRef(onCommit);
  commitRef.current = onCommit;

  const supported = Boolean(speechRecognition());

  const stop = React.useCallback(() => {
    try { recognizerRef.current?.stop(); } catch { /* already stopped */ }
    recognizerRef.current = null;
    setField(null);
  }, []);

  // A recogniser left running after the workspace unmounts keeps the microphone
  // open, which the browser shows as a tab that is permanently recording.
  React.useEffect(() => () => {
    try { recognizerRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  const start = React.useCallback((target) => {
    const Recognition = speechRecognition();
    if (!Recognition) return;
    stop();
    const recognizer = new Recognition();
    recognizer.lang = SPEECH_LANG;
    recognizer.continuous = true;
    // Only FINAL results are committed: an interim result is re-sent as it is
    // refined, and appending those to a textarea writes the same half-sentence
    // four times.
    recognizer.interimResults = false;
    recognizer.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (!event.results[i].isFinal) continue;
        const chunk = String(event.results[i][0]?.transcript || '').trim();
        if (chunk) commitRef.current?.(target, chunk);
      }
    };
    recognizer.onerror = () => setField(null);
    recognizer.onend = () => setField(null);
    recognizerRef.current = recognizer;
    setField(target);
    try { recognizer.start(); } catch { setField(null); }
  }, [stop]);

  const toggle = React.useCallback((target) => {
    if (field === target) stop();
    else start(target);
  }, [field, start, stop]);

  return { supported, listening: field, toggle, stop };
}

export default useDictation;
